import {
  alpha,
  filledInputClasses,
  formHelperTextClasses,
  formLabelClasses,
  inputBaseClasses,
  inputClasses,
  inputLabelClasses,
  menuClasses,
  outlinedInputClasses,
  selectClasses
} from "@mui/material"
import { makeStyles } from "tss-react/mui"
import {
  blueGrey,
  lightBlue,
  red
} from "../../../../mp-core/themes/palette"

type StylesParams = {
  inline?: boolean
  label?: boolean
  error?: boolean
  variant?: "outlined" | "filled"
  showLine?: boolean
}

const getFocusState = (color: string) => {
  return {
    outline: "none",
    boxShadow: `0px 0px 0px 1px ${color}, 0px 0px 0px 3px ${alpha(color, 0.2)}`
  }
}

// Greyed look shared by the native disabled state and the aria-disabled (isLoading) state.
const disabledAppearance = {
  [`& > .${selectClasses.select}`]: {
    WebkitTextFillColor: blueGrey[300]
  },

  [`& .${outlinedInputClasses.root}`]: {
    borderColor: blueGrey[100]
  },

  [`&.${filledInputClasses.root}`]: {
    backgroundColor: alpha(blueGrey[100], 0.7)
  },

  [`& .${selectClasses.icon}`]: {
    color: blueGrey[200]
  }
}

export const useStyles = makeStyles<StylesParams>()(
  (
    { spacing, transitions, palette, shadows },
    {
      inline = false,
      label = false,
      error = false,
      variant = "outlined",
      showLine = inline
    }
  ) => ({
    container: {
      display: "flex",

      ...(inline && {
        display: "inline-flex"
      }),

      "&:hover": {
        [`& .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: blueGrey[400]
        }
      },

      [`& .${formHelperTextClasses.root}`]: {
        margin: spacing(2, 4, 0),

        [`&.${formHelperTextClasses.error}`]: {
          color: red[500]
        }
      }
    },

    menu: {
      [`& .${menuClasses.paper}`]: {
        borderRadius: "2px"
      },
      [`& .${menuClasses.list}`]: {
        padding: spacing(1, 0),
        boxShadow: shadows[6]
      }
    },

    option: {
      padding: spacing(2, 3),
      fontSize: "0.875rem",
      lineHeight: "1.125rem",

      "&:hover": {
        backgroundColor: blueGrey[50]
      }
    },

    select: {
      outline: "none",
      borderRadius: "3px",
      transition: transitions.create(["background-color"]),

      // Nested elements
      [`& .${selectClasses.select}`]: {
        borderRadius: "2px",
        height: "1.625rem",
        fontSize: "1rem",
        lineHeight: "1.625rem",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",

        "&:focus": {
          boxShadow: "none !important",
          outline: "none"
        },

        ...(inline && {
          height: "1.125rem",
          fontSize: "0.875rem",
          lineHeight: "1.125rem",
          minHeight: "auto"
        }),

        // Need this additional classname to override default style.
        [`&.${inputBaseClasses.input}`]: {
          padding: spacing(2, 7, 2, 4),
          ...(label && !inline && { paddingTop: spacing(5) }),

          ...(inline && {
            padding: spacing(2, 3),
            paddingRight: "44px"
          })
        },

        [`&.${inputClasses.inputSizeSmall}`]: {
          height: "1.375rem",
          fontSize: "0.875rem",
          lineHeight: "1.375rem",

          ...(inline && {
            height: "1.25rem",
            lineHeight: "1.375rem"
          }),

          // Need this additional classname to override default style.
          [`&.${inputBaseClasses.input}`]: {
            padding: spacing(2, 7, 1, 4),

            ...(label && !inline && { paddingTop: spacing(5) }),

            ...(inline && {
              padding: spacing(1, 2),
              paddingRight: "40px"
            })
          }
        }
      },
      [`& .${selectClasses.icon}`]: {
        color: blueGrey[500],
        right: 0,
        top: 0,
        boxSizing: "content-box",
        width: "32px",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        ...(showLine && {
          borderLeft: `1px solid ${blueGrey[200]}`
        })
      },

      // Variant - outline
      [`& .${outlinedInputClasses.notchedOutline}`]: {
        transition: "none",
        border: "none",
        top: 0,

        "& > legend": {
          display: "none"
        }
      },

      [`&.${outlinedInputClasses.root}`]: {
        backgroundColor: palette.background.paper,
        border: `1px solid ${blueGrey[200]}`,

        ...(error && {
          ...getFocusState(red[400])
        }),

        ["&:hover"]: {
          borderColor: blueGrey[400]
        }
      },

      [`&.${filledInputClasses.root}`]: {
        backgroundColor: blueGrey[50],

        ...(error && {
          ...getFocusState(red[400])
        }),

        [`& .${filledInputClasses.input}:focus`]: {
          backgroundColor: blueGrey[50]
        }
      },

      // States
      [`&.${inputBaseClasses.focused}`]: {
        ...getFocusState(palette.focus),

        transition: transitions.create(["border-color", "box-shadow"]),

        [`&.${outlinedInputClasses.root}`]: {
          backgroundColor: palette.background.paper,
          borderColor: lightBlue[300],
          borderWidth: "1px",
          borderStyle: "solid"
        },

        [`&.${filledInputClasses.root}`]: {
          backgroundColor: blueGrey[50]
        }
      },
      [`&.${inputBaseClasses.disabled}`]: disabledAppearance
    },

    // a11y: identical to native disabled (isLoading is aria-disabled, not native disabled); only the spinner icon differs. pointerEvents matches disabled's no-hover while staying keyboard-focusable.
    ariaDisabled: {
      cursor: "default",
      pointerEvents: "none",
      ...disabledAppearance
    },

    label: {
      color: variant === "outlined" ? blueGrey[500] : blueGrey[600],
      left: "1px",
      top: spacing(0),
      fontSize: "1rem",
      lineHeight: "1.375rem",
      transform: "translate(16px, 19px) scale(1)",
      maxWidth: `calc(100% - ${spacing(4)} - ${spacing(7)})`,

      ...(inline && {
        fontSize: "0.875rem",
        lineHeight: "1.25rem",
        transform: "translate(12px, 8px) scale(1)",
        maxWidth: `calc(100% - ${spacing(3)} - 44px)`
      }),

      [`&.${inputLabelClasses.sizeSmall}`]: {
        fontSize: "0.875rem",
        lineHeight: "1.375rem",
        transform: "translate(16px, 16px) scale(1)",
        maxWidth: `calc(100% - ${spacing(4)} - ${spacing(7)})`,

        ...(inline && {
          transform: "translate(8px, 5px) scale(1)",
          maxWidth: `calc(100% - ${spacing(2)} - 40px)`
        })
      },

      [`&.${inputLabelClasses.focused}`]: {
        color: blueGrey[500]
      },

      [`&.${inputLabelClasses.disabled}`]: {
        color: blueGrey[400]
      },

      [`&.${formLabelClasses.filled}`]: {
        fontSize: "0.75rem",
        lineHeight: "0.875rem",
        transform: "translate(16px, 10px) scale(1)",

        [`&.${inputLabelClasses.sizeSmall}`]: {
          transform: "translate(16px, 8px) scale(1)"
        },

        ...(inline && { display: "none" })
      }
    },

    placeholder: {
      color: blueGrey[500]
    },

    selectValue: {
      overflow: "hidden",
      textOverflow: "ellipsis"
    }
  })
)
